import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faPlane } from '@fortawesome/free-solid-svg-icons';
import { RateOption, rateService } from '../../services/rate.service';
import styles from '../../styles/common/ServiceCard.module.css';

// Carrier Logos
import libertyExpressIMG from "../../assets/carriers/libertyExpress.png";
import krakenIMG from "../../assets/carriers/kraken.png";
import upsIMG from "../../assets/carriers/ups.png";

interface ServiceCardProps {
    rateOption: RateOption;
    isSelected: boolean;
    isCurrentSelectedService?: boolean;
    estimatedTotal: number;
    selectedExternalIndices: Set<number>;
    onSelectExternalService: (newIndices: Set<number>) => void;
    onViewDetails: () => void;
    onClick?: () => void;
    badge?: {
        text: string;
        type?: 'primary' | 'success';
    };
    estimatedArrivalText?: string;
    cleanCard?: boolean;
}

export const ServiceCard: React.FC<ServiceCardProps> = ({
    rateOption,
    isSelected,
    isCurrentSelectedService = false,
    estimatedTotal,
    selectedExternalIndices,
    onSelectExternalService,
    onViewDetails,
    onClick,
    badge,
    estimatedArrivalText,
    cleanCard = false,
}) => {
    const handleServiceSelect = (index: number) => {
        const nextIndices = rateService.toggleExternalService(
            rateOption,
            selectedExternalIndices,
            index
        );
        onSelectExternalService(nextIndices);
    };

    const getCarrierLogo = (carrierName: string) => {
        if (carrierName.toLowerCase().includes("liberty")) {
            return libertyExpressIMG;
        }
        if (carrierName.toLowerCase().includes("kraken")) {
            return krakenIMG;
        }
        if (carrierName.toLowerCase().includes("ups")) {
            return upsIMG;
        }
        return `https://placehold.co/200x80?text=${encodeURIComponent(carrierName)}`;
    };

    if (cleanCard) {
        return (
            <div
                className={`${styles.serviceCard} ${isSelected ? styles.selected : ''}`}
                onClick={onClick}
                style={{
                    padding: '24px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '160px',
                    border: isSelected ? '2px solid var(--brand-yellow, #ffc72c)' : '1.5px solid #e2e8f0',
                    borderRadius: '16px',
                    background: '#fff',
                    cursor: 'pointer'
                }}
            >
                <div className={styles.logoContainer} style={{ marginBottom: '12px' }}>
                    <img
                        src={getCarrierLogo(rateOption.carrierName)}
                        alt={`${rateOption.carrierName} logo`}
                        className={styles.carrierLogo}
                        style={{ maxHeight: '60px', objectFit: 'contain' }}
                        onError={(e) => {
                            (e.target as HTMLImageElement).src = `https://placehold.co/150x50?text=${rateOption.carrierName.substring(0, 3)}`;
                        }}
                    />
                </div>
                {rateOption.carrierName.toUpperCase() !== "UPS" && (
                    <div className={styles.carrierName} style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b', textAlign: 'center' }}>
                        {rateOption.carrierName}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            className={`${styles.serviceCard} ${isSelected ? styles.selected : ''} ${isCurrentSelectedService ? styles.currentSelected : ''}`}
            onClick={onClick}
        >
            <button
                type="button"
                className={styles.infoIconBtn}
                onClick={(e) => {
                    e.stopPropagation();
                    onViewDetails();
                }}
                title="Ver Detalles"
            >
                <FontAwesomeIcon icon={faInfoCircle} />
            </button>
            <div className={styles.logoContainer}>
                <img
                    src={getCarrierLogo(rateOption.carrierName)}
                    alt={`${rateOption.carrierName} logo`}
                    className={styles.carrierLogo}
                    onError={(e) => {
                        (e.target as HTMLImageElement).src = `https://placehold.co/150x50?text=${rateOption.carrierName.substring(0, 3)}`;
                        (e.target as HTMLImageElement).style.backgroundColor = "#f0f0f0";
                    }}
                />
            </div>
            <div className={styles.wayBadge}>
                <FontAwesomeIcon icon={faPlane} />
                <span>AÉREO</span>
            </div>
            <div className={styles.carrierHeader}>
                <div className={styles.carrierInfo}>
                    <div className={styles.carrierName}>
                        {rateOption.carrierName}
                        {rateOption.serviceIndicator && (
                            <span className={styles.serviceIndicator}> ({rateOption.serviceIndicator})</span>
                        )}
                    </div>
                    {badge && (
                        <div className={`${styles.badge} ${badge.type === 'success' ? styles.success : styles.primary}`}>
                            {badge.text}
                        </div>
                    )}
                    {estimatedArrivalText ? (
                        <div className={styles.estimatedArrivalDate}>
                            {estimatedArrivalText}
                        </div>
                    ) : (
                        rateOption.estimatedMinDays && rateOption.estimatedMaxDays && (
                            <div className={styles.estimatedArrivalDate}>
                                Entrega estimada: {rateOption.estimatedMinDays} - {rateOption.estimatedMaxDays} días
                            </div>
                        )
                    )}
                </div>
                <div className={styles.estimatedTotal}>
                    ${estimatedTotal.toFixed(2)} <span className={styles.currencySuffix}>{rateOption.currency}</span>
                    {rateOption.discountPercent && rateOption.discountPercent > 0 && (
                        <span className={styles.discountBadge}>
                            {rateOption.discountPercent.toFixed(2)}% de Descuento
                        </span>
                    )}
                </div>
                {rateOption.standardRate && rateOption.discountPercent && rateOption.discountPercent > 0 && (
                    <div className={styles.standardPriceRow}>
                        <span className={styles.standardPriceLabel}>Tarifa regular: </span>
                        <span className={styles.strikethroughPrice}>
                            ${rateOption.standardRate.toFixed(2)} {rateOption.currency}
                        </span>
                    </div>
                )}
            </div>

            {rateOption.externalServices && rateOption.externalServices.length > 0 && (
                <div className={styles.externalServicesSection}>
                    <div className={styles.externalServicesTitle}>Servicios Adicionales</div>
                    {(() => {
                        let globalIndex = 0;
                        return rateOption.externalServices.map((provider, pIdx) => (
                            <div key={pIdx} className={styles.providerGroup}>
                                <div className={styles.providerHeader}>
                                    {provider.providerName} {provider.isRequired && <span className={styles.requiredBadge}>(Requerido)</span>}
                                </div>
                                {provider.options.map((option, oIdx) => {
                                    const currentIndex = globalIndex++;
                                    const isMandatory = option.mandatory;
                                    const isChecked = selectedExternalIndices.has(currentIndex);

                                    return (
                                        <div key={oIdx} className={styles.externalServiceItem}>
                                            <label className={styles.customCheckbox}>
                                                <input
                                                    type="checkbox"
                                                    name={`provider-${rateOption.carrierName}-${pIdx}`}
                                                    checked={isChecked}
                                                    disabled={isMandatory && provider.options.length === 1 && provider.isRequired}
                                                    onChange={() => handleServiceSelect(currentIndex)}
                                                />
                                                <span className={styles.checkboxCheckmark}></span>
                                                <span>{option.serviceName}</span>
                                            </label>
                                            <span className={styles.externalServicePrice}>
                                                +{option.serviceSubTotal.toFixed(2)} {rateOption.currency}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        ));
                    })()}
                </div>
            )}
        </div>
    );
};

export default ServiceCard;
